/**
 * Подписи и счёт мастера разбора порций — вся логика без JSX (AK-546, ч.2).
 *
 * Здесь язык агронома, а не модели: «чем занято поле?» вместо «target_kind»,
 * «нужен человек» вместо «manual». Держим отдельно от компонентов, чтобы
 * формулировки и арифметику прогресса можно было проверить тестом, а не
 * глазами по скриншоту.
 */
import type {
  BatchCounts,
  BatchSourceKind,
  ItemBucket,
  ReviewEntityType,
  TargetKind,
} from '@/lib/reference/catalogReview';

// ─── склонения ───────────────────────────────────────────────────────────────

/** Русское склонение по числу: `plural(2,'название','названия','названий')`. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** «за названием стоит 3 451 посев» / «412 работ» — вес строки для человека. */
export function weightLabel(n: number, entityType: ReviewEntityType): string {
  if (n <= 0) return entityType === 'operation_type' ? 'нет работ' : 'нет посевов';
  return entityType === 'operation_type'
    ? `${n} ${plural(n, 'работа', 'работы', 'работ')}`
    : `${n} ${plural(n, 'посев', 'посева', 'посевов')}`;
}

/** «2019–2026» / «2022» / '' — годы, за которые название встречалось. */
export function yearsLabel(from: number | null, to: number | null): string {
  if (from === null && to === null) return '';
  if (from !== null && to !== null && from !== to) return `${from}–${to}`;
  return String(from ?? to);
}

// ─── источники порции ────────────────────────────────────────────────────────

export const SOURCE_LABEL: Record<BatchSourceKind, string> = {
  agrohistory: 'АгроХистори',
  excel: 'Эксель',
  one_c: '1С',
  manual: 'Вручную',
  // Не «ретро» — человеку это слово ничего не говорит.
  retro: 'Накоплено раньше',
};

export function sourceLabel(kind: string): string {
  return SOURCE_LABEL[kind as BatchSourceKind] ?? kind;
}

/**
 * Честный источник строки — то, что человек и хочет увидеть.
 *
 * UAT founder 08.08: «Накоплено раньше» — не источник. Если названия приехали
 * из АгроХистори (это видно по самим записям справочника), так и пишем:
 * «Импорт истории АгроХистори (разовый)». «Накоплено раньше» остаётся ТОЛЬКО
 * для случая, когда происхождение действительно неизвестно — врать в другую
 * сторону («интеграция», которой не было) ещё хуже.
 */
export function originLabel(batch: {
  source_kind: string;
  origin_kind?: string | null;
}): string {
  if (batch.source_kind !== 'retro') return sourceLabel(batch.source_kind);
  if (!batch.origin_kind) return SOURCE_LABEL.retro;
  return `Импорт истории ${sourceLabel(batch.origin_kind)} (разовый)`;
}

/**
 * Настоящая дата данных: у накопленного раньше `received_at` — это момент
 * обновления системы, а не приезда данных. Тогда берём дату самих записей.
 */
export function originDate(batch: {
  source_kind: string;
  origin_kind?: string | null;
  origin_to?: string | null;
  received_at: string;
}): string {
  if (batch.source_kind === 'retro' && batch.origin_kind && batch.origin_to) {
    return batch.origin_to;
  }
  return batch.received_at;
}

// ─── бакеты сверки ───────────────────────────────────────────────────────────

export const BUCKET_LABEL: Record<ItemBucket, string> = {
  existing: 'Уже есть в справочнике',
  suggested: 'Платформа предложила',
  manual: 'Нужен человек',
};

// ─── шаги мастера ────────────────────────────────────────────────────────────

export type WizardStep = 'incoming' | 'existing' | 'suggested' | 'manual' | 'done';

export const WIZARD_STEPS: ReadonlyArray<{ key: WizardStep; title: string }> = [
  { key: 'incoming', title: 'Что пришло' },
  { key: 'existing', title: 'Уже есть в справочнике' },
  { key: 'suggested', title: 'Платформа предложила' },
  { key: 'manual', title: 'Нужен человек' },
  { key: 'done', title: 'Готово' },
];

/** Подпись под шагом: «135 названий», «100 — просто посмотреть», … */
export function stepHint(step: WizardStep, counts: BatchCounts): string {
  switch (step) {
    case 'incoming':
      return `${counts.total} ${plural(counts.total, 'название', 'названия', 'названий')}`;
    case 'existing':
      return `${counts.existing} — просто посмотреть`;
    case 'suggested':
      return `${counts.suggested} — согласиться`;
    case 'manual':
      return `${counts.manual} — разобрать самому`;
    case 'done':
      return 'возврат в справочник';
  }
}

/**
 * Прогресс мастера — от ТРЕБУЮЩИХ разбора, а не от всего пришедшего.
 *
 * Правка founder 07.08: из 135 названий 100 уже заведены, и показывать человеку
 * 135 строк работы — враньё. Честная цифра — 35.
 */
export function progressPct(counts: BatchCounts): number {
  if (counts.needs_review <= 0) return 100;
  return Math.round((counts.decided / counts.needs_review) * 100);
}

/**
 * Сколько строк РЕАЛЬНО ещё ждёт человека.
 *
 * Считаем по строкам, которые остались нерешёнными в рабочих корзинах
 * («платформа предложила» + «нужен человек»), а не вычитанием «сколько нужно
 * минус сколько разнесено»: арифметика на глаз разъедется в тот момент, когда
 * появится ещё один исход строки, а число — главное на экране.
 * ``pending`` — запасной вариант для ответов, где детальных счётчиков нет.
 */
export function remainingToReview(counts: BatchCounts): number {
  const detailed = (counts.pending_suggested ?? 0) + (counts.pending_manual ?? 0);
  return detailed > 0 ? detailed : (counts.pending ?? 0);
}

/**
 * «Осталось разобрать: 19 из 76 · разнесено 57».
 *
 * UAT founder 10.08: «по идее там надо писать, сколько осталось». Раньше первым
 * числом шло «Разнесено 57 из 76» — сделанное, а не оставшееся. Человек
 * приходит на этот экран с вопросом «мне ещё сколько?», и отвечать ему надо
 * ровно на него; сделанное остаётся рядом, вторым планом.
 */
export function progressLabel(counts: BatchCounts): string {
  if (counts.needs_review <= 0) return 'Разбирать нечего — всё уже есть в справочнике';
  const left = remainingToReview(counts);
  if (left === 0) {
    return `Разобрано полностью — ${counts.decided} из ${counts.needs_review}`;
  }
  return `Осталось разобрать: ${left} из ${counts.needs_review} · разнесено ${counts.decided}`;
}

// ─── «Чем занято поле?» (Р-5) ────────────────────────────────────────────────

export interface TargetDoor {
  key: TargetKind;
  title: string;
  hint: string;
}

/** Пять дверей для культур — родитель «занятие поля», сиблинги под ним. */
export const CROP_DOORS: ReadonlyArray<TargetDoor> = [
  { key: 'crop', title: 'Культура', hint: 'растёт, сеют и убирают, есть сорт и урожай' },
  { key: 'crop_mix', title: 'Смесь культур', hint: 'две и больше культур, посеянных вместе' },
  { key: 'field_state', title: 'Пар или АМП', hint: 'решили не сеять: обработки есть, урожая нет' },
  { key: 'land_category', title: 'Угодье', hint: 'сад, виноградник, сенокос, пастбище — на много лет' },
  { key: 'not_needed', title: 'Не нужно в справочнике', hint: 'служебная строка чужой системы' },
];

/** У тех.операций вопрос другой — «что это за работа?». */
export const OPERATION_DOORS: ReadonlyArray<TargetDoor> = [
  { key: 'operation_type', title: 'Тех.операция', hint: 'полевая, логистика или вспомогательная работа' },
  { key: 'not_needed', title: 'Не нужно в справочнике', hint: 'служебные строки выгрузки' },
];

export function doorsFor(entityType: ReviewEntityType): ReadonlyArray<TargetDoor> {
  return entityType === 'operation_type' ? OPERATION_DOORS : CROP_DOORS;
}

/** Заголовок вопроса замены — у справочников он разный. */
export function doorsQuestion(entityType: ReviewEntityType): string {
  return entityType === 'operation_type' ? 'Что это за работа?' : 'Чем занято поле?';
}

/** Бейдж типа записи в общем списке справочника (решение А). */
export function typeBadgeLabel(kind: string | null, hasComponents: boolean): string {
  if (hasComponents || kind === 'crop_mix') return 'смесь';
  if (kind === 'field_state') return 'пар / АМП';
  if (kind === 'land_parcel') return 'угодье';
  return 'культура';
}

// ─── состояние шага «Готово» ─────────────────────────────────────────────────

/**
 * Итог порции для финального экрана.
 *
 * `finished === null` значит «пользователь просто кликнул по степперу, а
 * "Завершить разбор" не нажимал». Это НЕ повод рисовать «Порция разобрана
 * полностью»: ощущение конца не подделываем — на 28 из 35 человек должен
 * видеть свои 28 из 35, а не поздравление.
 */
export function doneHeadline(
  counts: BatchCounts,
  finished: { fully: boolean } | null,
): string {
  if (finished?.fully === true) {
    return `Разобрано полностью — ${counts.decided} из ${counts.needs_review}`;
  }
  return progressLabel(counts);
}

/**
 * Можно ли открыть шаг кликом по степперу.
 *
 * Все шаги — свободная навигация, КРОМЕ «Готово»: он существует только как
 * результат нажатия «Завершить разбор». Иначе клик по пятому кружку выдавал бы
 * экран завершения у незавершённой порции.
 */
export function canOpenStep(
  step: WizardStep,
  finished: unknown | null,
): boolean {
  return step !== 'done' || finished !== null;
}

/** «Показано 100 из 641» — усечение выдачи должно быть видно, а не молчать. */
export function shownOfLabel(shown: number, total: number): string {
  return `Показано ${shown} из ${total}`;
}
